import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Lenis from 'lenis';

const ExperienceContext = createContext({
  ready: false,
  setReady: () => {},
  scrollTo: () => {},
  lock: () => {},
  unlock: () => {},
});

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Smooth scroll (Lenis) + estado da intro + trava de scroll para modais.
 */
export const ExperienceProvider = ({ children }) => {
  const lenisRef = useRef(null);
  const locks = useRef(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
    lenisRef.current = lenis;
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  const scrollTo = useCallback((target, opts = {}) => {
    const el = typeof target === 'string' ? document.getElementById(target) : target;
    const top = target === 'top' || target === 0;
    if (!el && !top) return;
    const lenis = lenisRef.current;
    if (lenis) {
      lenis.scrollTo(top ? 0 : el, { offset: opts.offset ?? 0, duration: 1.4, force: true });
    } else if (top) {
      window.scrollTo({ top: 0 });
    } else {
      el.scrollIntoView();
    }
  }, []);

  const lock = useCallback(() => {
    locks.current += 1;
    lenisRef.current?.stop();
    document.documentElement.style.overflow = 'hidden';
  }, []);

  const unlock = useCallback(() => {
    locks.current = Math.max(0, locks.current - 1);
    if (locks.current === 0) {
      lenisRef.current?.start();
      document.documentElement.style.overflow = '';
    }
  }, []);

  const value = useMemo(
    () => ({ ready, setReady, scrollTo, lock, unlock }),
    [ready, scrollTo, lock, unlock]
  );

  return createElement(ExperienceContext.Provider, { value }, children);
};

export const useExperience = () => useContext(ExperienceContext);

/** Trava o scroll da página enquanto `active` for verdadeiro. */
export const useScrollLock = (active) => {
  const { lock, unlock } = useExperience();
  useEffect(() => {
    if (!active) return undefined;
    lock();
    return unlock;
  }, [active, lock, unlock]);
};

/** true em dispositivos com mouse (hover real). */
export const useFinePointer = () => {
  const [fine, setFine] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const update = () => setFine(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return fine;
};

/**
 * true assim que o topo do elemento passa de `offset` da altura da tela — e continua true.
 * Diferente do whileInView, não "perde" elementos pulados por scroll rápido ou reload no meio da página.
 */
export const useSeen = (ref, offset = 0.9) => {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (seen) return undefined;
    let frame = null;
    const check = () => {
      frame = null;
      const el = ref.current;
      if (el && el.getBoundingClientRect().top < window.innerHeight * offset) setSeen(true);
    };
    const onScroll = () => {
      if (frame === null) frame = requestAnimationFrame(check);
    };
    check();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [ref, offset, seen]);
  return seen;
};

/**
 * true enquanto o elemento está perto/na tela. Usado para pausar animações
 * infinitas fora da tela (framer via `live`, CSS via data-paused).
 */
export const useLive = (ref, margin = '200px 0px') => {
  const [live, setLive] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([entry]) => setLive(entry.isIntersecting), { rootMargin: margin });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, margin]);
  return live;
};

export const EASE = [0.16, 1, 0.3, 1];
